import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, signal, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BadgeComponent } from '../../../../shared/components/badge/badge.component';
import { ButtonComponent } from '../../../../shared/components/button/button.component';
import { DataNotFoundComponent } from '../../../../shared/components/data-not-found/data-not-found.component';
import { TabComponent } from '../../../../shared/components/tabs/tab/tab.component';
import { TabsComponent } from '../../../../shared/components/tabs/tabs.component';
import { TagComponent } from '../../../../shared/components/tag/tag.component';
import { Equipments } from '../../../../shared/enums/equipments';
import { ClientTransportationContact, Contact, Destination, Equipment, IBlueprintSheetCargo, PlaceInstruction, Waypoint } from '../../../../shared/interfaces/blueprint-sheet';
import { RideStopDocumentDto } from '../../../../shared/interfaces/ride-firestore';
import { IStep } from '../../../../shared/interfaces/step.interface';
import { StateService } from '../../../../shared/services/state.service';
import moment from 'moment';
import { AccordionModule } from 'primeng/accordion';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { MapRideServiceService } from '../../services/map-ride-service.service';
import { BasicTableComponent } from '../basic-table/basic-table.component';
import { BasicTablePipe } from '../basic-table/basic-table.pipe';
import { MapDialogComponent } from '../map-dialog/map-dialog.component';
import { CalcDriverPayoutSheetPipe } from './calc-driver-payout-sheet.pipe';
import { cargoActionType } from '../../../../shared/enums/rideStopCargoStatus';
import { AuthService } from '../../../../core/auth/services/auth.service';
import { UserRole } from '../../../../core/auth/enums/userRoles';
import { StopPlaceType } from '../../../../shared/enums/common';

export interface StudentRequirementsAndInstructions {
  fullName?: string;
  preferredDriverGender: string;
  isSensitiveRide: boolean;
  isSoloRider: boolean;
  needsClientProvidedAttendant: boolean;
  needsPlatformProvidedAttendant: boolean;
  preferredAttendantSex: string;
  needsWheelchairVehicle: boolean;
  pickupInstructionsNote: string;
  dropOffInstructionsNote: string;
  onboardInstructionsNote: string;
  specialInstructionsNote: string;
  otherInstructionsNote: string[];
  placeInstructions: {
    pickUpInstruction: string;
    dropOffInstruction: string;
    otherInstructions: string[];
  };
}

export enum PreferredDriverGender {
  NoPreference = 84,
  Male = 98,
  Female = 99,
}

@Component({
  selector: 'app-ride-blueprint-dialog',
  standalone: true,
  imports: [
    ButtonComponent,
    CommonModule,
    TabsComponent,
    TabComponent,
    BasicTableComponent,
    AccordionModule,
    BadgeComponent,
    TagComponent,
    FormsModule,
    InputTextModule,
    TooltipModule,
    CalcDriverPayoutSheetPipe,
    BasicTablePipe,
    DataNotFoundComponent,
    TooltipModule,
  ],
  providers: [DialogService],
  templateUrl: './ride-blueprint-dialog.component.html',
  styleUrl: './ride-blueprint-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RideBlueprintDialogComponent implements OnInit {
  ref = inject(DynamicDialogRef);
  config = inject(DynamicDialogConfig);
  private readonly dialogService = inject(DialogService);
  private readonly stateService = inject(StateService);
  private readonly mapRideService = inject(MapRideServiceService);
  private readonly authService = inject(AuthService);

  accordionActiveIndex: number[] = [0];

  serviceProviderPay: any = 0;
  driverProvidePay: any = 0;
  adjustedStopsTimeline: IStep[] = [];

  equipments = [];
  students = [];
  destinations = [];
  calenders = [];
  placeInstructions = [];
  clientContacts = [];
  cargoActionType = signal(cargoActionType);
  stopPlaceType = signal(StopPlaceType);

  studentInstructions: StudentRequirementsAndInstructions[] = [];

  appSettings = this.authService.appSetting();
  currentRole = (localStorage.getItem('role') ?? '').toLowerCase();
  userRole = UserRole;
  ngOnInit() {
    console.log(this.config.data);

    this.mapEquipments();
    this.mapStudents();
    this.mapDestinations();
    this.mapCalender();
    this.mapPlaceInstructions();
    this.mapClientContacts();
    this.mapStudentsInstructions();
    this.formatWaypointTime();

    this.serviceProviderPay = this.config.data.blueprintSheetData?.ContractorOriginalPayout?.toFixed(2);

    this.driverProvidePay = Number(this.config.data.driverPayout)?.toFixed(2);
    // remove time anchor eta adjustment from the rendered blueprint
    this.adjustedStopsTimeline = this.adjustStepTimes(this.config.data.rideStops, this.config.data.rideStopsDoc);
  }

  mapEquipments() {
    this.equipments = this.config.data.blueprintSheetData.Equipment.filter((equipment: Equipment) => equipment.NumberOfEquipment > 0).map((equipment: Equipment) => {
      const equipmentId = Equipments[equipment.Name?.replaceAll(' ', '') as keyof typeof Equipments];
      const base64Img = this.stateService.getEquipmentsFiles().find((e: any) => e.equipmenId == equipmentId)?.base64File;
      return {
        equipmentType: equipment.Name,
        quantity: equipment.NumberOfEquipment,
        icon: base64Img ? `data:image/jpeg;base64, ${base64Img}` : 'no-icon',
      };
    });
  }

  mapPlaceInstructions() {
    this.placeInstructions = this.config.data.blueprintSheetData.PlaceInstructions.map((inst: PlaceInstruction) => {
      return {
        type: inst.Type,
        description: inst.Description,
      };
    });
  }

  mapStudentsInstructions() {
    this.studentInstructions = this.config.data.blueprintSheetData.Cargo.filter((c: IBlueprintSheetCargo) => c.CargoType == 'Student').map((s: IBlueprintSheetCargo) => {
      let preferredDriverGender = 'No Preference';
      if (s.PreferredDriverGenderId == PreferredDriverGender.Male) {
        preferredDriverGender = 'Male';
      } else if (s.PreferredDriverGenderId == PreferredDriverGender.Female) {
        preferredDriverGender = 'Female';
      }

      // Filter place instructions for this student that are visible to drivers
      const relevantInstructions = this.config.data.blueprintSheetData.PlaceInstructions.filter((inst: PlaceInstruction) => inst.PlaceId == s.PlaceId && JSON.parse(inst.Visibility ?? '[]').includes('Drivers'));

      // Aggregate instructions by type
      const placeInstructions = {
        pickUpInstruction: relevantInstructions.find((inst: PlaceInstruction) => inst.Type == 'Pick Up')?.Description ?? '',
        dropOffInstruction: relevantInstructions.find((inst: PlaceInstruction) => inst.Type == 'Drop Off')?.Description ?? '',
        otherInstructions: relevantInstructions.filter((inst: PlaceInstruction) => inst.Type != 'Pick Up' && inst.Type != 'Drop Off').map((inst: PlaceInstruction) => inst.Description),
      };

      const instructions: StudentRequirementsAndInstructions = {
        fullName: s.FullName,
        preferredDriverGender: preferredDriverGender,
        isSensitiveRide: s.IsSensitiveRide ?? false,
        isSoloRider: s.IsSoloRider ?? false,
        needsClientProvidedAttendant: s.NeedsClientProvidedAttendant ?? false,
        needsPlatformProvidedAttendant: s.NeedsPlatformProvidedAttendant ?? false,
        preferredAttendantSex: s.PreferredAttendantSex ?? '',
        needsWheelchairVehicle: s.NeedsWheelchairVehicle == 1,
        pickupInstructionsNote: s.PickupInstructionsNote ?? '',
        dropOffInstructionsNote: s.DropOffInstructionsNote ?? '',
        onboardInstructionsNote: s.OnboardInstructionsNote ?? '',
        specialInstructionsNote: s.SpecialInstructionsNote ?? '',
        otherInstructionsNote: s.OtherInstructionsNote?.map(o => o.Description) ?? [],
        placeInstructions: placeInstructions,
      };
      return instructions;
    });
  }

  mapClientContacts() {
    this.clientContacts = this.config.data.blueprintSheetData.ClientTransportationContacts.map((c: ClientTransportationContact) => {
      return {
        id: c.CLientId,
        name: c.Name,
        type: c.ContactType,
        phone: this.mapRideService.formatPhoneNumber(c.PhoneNumber ?? ''),
        email: c.Email,
      };
    });
  }

  mapDestinations() {
    this.destinations = this.config.data.blueprintSheetData.Destinations.map((d: Destination) => {
      const destination = {
        name: d.Name,
        contacts: d.Contacts?.map(c => {
          return {
            name: c.FullName,
            relation: c.ContactType,
            number: this.mapRideService.formatPhoneNumber(c.PhoneNumber ?? ''),
          };
        }),
        notes: d.Note,
      };
      if (this.currentRole == UserRole.Provider) delete destination.contacts;
      return destination;
    });
  }

  mapCalender() {
    this.calenders = this.config.data.blueprintSheetData.Destinations.map((d: Destination) => {
      return {
        calendar: d.PlaceScheduleName,
        details: d.PlaceScheduleNickName ? `(${d.PlaceScheduleNickName}) - ${d.Name}` : 'N/A',
      };
    });
  }

  formatWaypointTime() {
    this.config.data.blueprintSheetData.Waypoints = this.config.data.blueprintSheetData.Waypoints.map((x: Waypoint) => {
      return {
        ...x,
        weeklyAdjustments: this.mapRideService.getWeeklyAdjustment(this.config.data.blueprintSheetData.ScheduleTimeAdjustments, x.ScheduledArrivalTime ?? ''),
        ScheduledArrivalTime: moment(x.ScheduledArrivalTime).format('hh:mm A'),
      };
    });
  }

  mapStudents() {
    this.students = this.config.data.blueprintSheetData.Cargo.filter((c: IBlueprintSheetCargo) => c.CargoType == 'Student').map((s: IBlueprintSheetCargo) => {
      const instructions = [];
      if (s.DropOffInstructionsNote) instructions.push({ type: 'Drop Off', description: s.DropOffInstructionsNote });
      if (s.PickupInstructionsNote) instructions.push({ type: 'Pick Up', description: s.PickupInstructionsNote });
      if (s.OnboardInstructionsNote) instructions.push({ type: 'Onboard', description: s.OnboardInstructionsNote });
      if (s.SpecialInstructionsNote) instructions.push({ type: 'Special', description: s.SpecialInstructionsNote });
      if (s.OtherInstructionsNote && s.OtherInstructionsNote.length > 0) {
        s.OtherInstructionsNote.forEach(b => {
          instructions.push({ type: 'Other', description: b.Description });
        });
      }
      const student = {
        name: s.FullName,
        contacts: s.Contacts?.map((contact: Contact) => {
          return {
            name: contact.FullName,
            relation: contact.RelationshipToEntity,
            number: this.mapRideService.formatPhoneNumber(contact.PhoneNumber ?? ''),
            isPrimary: contact.IsPrimaryContact,
          };
        }),
        equipment: s.Equipment?.map(e => ({ name: e.Name })),
        studentInstructions: instructions,
      };
      if (this.currentRole == UserRole.Provider) delete student.contacts;
      return student;
    });
  }

  adjustTimeByMinutes(timeString: string | undefined, minutesToAdd: number): string | undefined {
    if (!timeString) return undefined;

    const [time, period] = timeString.split(' ');
    const [hours, minutes] = time.split(':').map(Number);

    const date = new Date();
    date.setHours(period === 'PM' && hours !== 12 ? hours + 12 : hours, minutes + minutesToAdd);

    const adjustedHours = date.getHours();
    const adjustedMinutes = date.getMinutes();
    const adjustedPeriod = adjustedHours >= 12 ? 'PM' : 'AM';
    const formattedHours = (adjustedHours % 12 || 12).toString().padStart(2, '0');
    const formattedMinutes = adjustedMinutes.toString().padStart(2, '0');

    return `${formattedHours}:${formattedMinutes} ${adjustedPeriod}`;
  }

  adjustStepTimes(steps: IStep[], rideStop: RideStopDocumentDto[]): IStep[] {
    const adjustmentMinutes = -(rideStop[0].ride_stop_times?.time_anchor_eta_adjustment_in_minutes ?? 0);
    return steps.map(step => ({
      ...step,
      time: this.adjustTimeByMinutes(step.time, adjustmentMinutes),
    }));
  }

  toggleActiveAccordionTab(index: number) {
    const currentIndex = this.accordionActiveIndex.indexOf(index);

    if (currentIndex != -1) {
      this.accordionActiveIndex = [...this.accordionActiveIndex.slice(0, currentIndex), ...this.accordionActiveIndex.slice(currentIndex + 1)];
    } else {
      this.accordionActiveIndex = [...this.accordionActiveIndex, index];
    }
  }

  accordionActiveIndexChange(index: string | number | string[] | number[] | null | undefined) {
    if (Array.isArray(index)) {
      this.accordionActiveIndex = index as number[];
    } else if (index == null) {
      this.accordionActiveIndex = [];
    } else {
      this.accordionActiveIndex = [index] as number[];
    }
  }

  expandAll() {
    this.accordionActiveIndex = Array.from(new Array(this.config.data.rideStops.length).keys());
  }

  openMapDialog() {
    this.dialogService.open(MapDialogComponent, {
      showHeader: false,
      width: '800px',
      modal: true,
      styleClass: 'dispatch-portal-custom-dialog',
      breakpoints: {
        '992px': '600px',
        '640px': '400px',
        '575px': '90vw',
      },
      data: {
        id: this.config.data.id,
        showRideTimeline: false,
        rideTimeline: this.config.data.rideStops,
        rideDOT: this.config.data.rideDOT,
        driverDetails: this.config.data.driverDetails,
      },
    });
  }
}
